import { useContext, useState } from "react";
import { useLocalization } from "@fluent/react";
import {
  Button,
  Chip,
  ContentCard,
  Popover,
  TruncatedAccount,
} from "@narmi/design_system";
import { Dialog } from "cerulean";
import { useNotificationContext } from "cerulean/src/NotificationContext";
import {
  formatCurrencyFromCents,
  formatDate,
} from "../../../PaymentActivity/CreateBulk/passthroughFormat";
import { triggerBlobDownload } from "../../../PaymentActivity/WireFileImport/helpers";
import AccountContext from "../../../contexts/AccountContext";
import DrawerRows from "../DrawerRow";
import type { DrawerRowProps } from "../DrawerRow";
import styles from "../../DualApproval.module.scss";
import type { NachaPassthroughTopCardProps } from "./types";

type OriginatingAccount =
  NachaPassthroughTopCardProps["details"]["originatingAccounts"][number];

const DetailValues = ({
  getLabel,
  values,
}: {
  getLabel: (count: number) => string;
  values: string[];
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [firstValue] = values;
  const hiddenValues = values.slice(1);

  return (
    <div className={styles.passthroughValues}>
      <span className={styles.passthroughVisibleValues}>{firstValue}</span>
      {hiddenValues.length ? (
        <div className={styles.passthroughOverflowTrigger}>
          <Popover
            content={
              isPopoverOpen ? (
                <div className={styles.passthroughOverflowValues}>
                  <div className={styles.passthroughOverflowHeading}>
                    {getLabel(hiddenValues.length)} · {hiddenValues.length}
                  </div>
                  <div className={styles.passthroughOverflowBadges}>
                    {hiddenValues.map((value) => (
                      <Chip
                        key={value}
                        kind="primary"
                        label={value}
                        isLabelSelectable
                      />
                    ))}
                  </div>
                </div>
              ) : null
            }
            side="top"
            wrapperDisplay="inline-flex"
            testId="nacha-passthrough-overflow"
            isOpen={isPopoverOpen}
            onUserEnable={() => setIsPopoverOpen(true)}
            onUserDismiss={() => setIsPopoverOpen(false)}
          >
            <span className={styles.passthroughOverflowCount}>
              +{hiddenValues.length}
            </span>
          </Popover>
        </div>
      ) : null}
    </div>
  );
};

const OriginatingAccountValues = ({
  originatingAccounts,
}: {
  originatingAccounts: OriginatingAccount[];
}) => {
  const { l10n } = useLocalization();
  const { accounts } = useContext(AccountContext);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const accountValues = originatingAccounts.map(({ accountId, lastFour }) => {
    const account = accountId
      ? accounts?.find(({ id }: { id: API.AccountId }) => id === accountId)
      : undefined;

    return {
      key: `${accountId ?? ""}:${lastFour}`,
      name: account?.name ?? "",
      lastFour: account?.getMaskedNumber() ?? lastFour,
    };
  });

  const renderAccount = ({
    key,
    name,
    lastFour,
  }: (typeof accountValues)[number]) => (
    <TruncatedAccount key={key} name={name} lastFour={lastFour} />
  );

  if (accountValues.length === 1) {
    return renderAccount(accountValues[0]);
  }

  return (
    <>
      <Button
        kind="plain"
        size="s"
        onClick={() => setIsDialogOpen(true)}
        testId="nacha-passthrough-view-accounts"
      >
        {l10n.getString("view-accounts-cta", {
          total: accountValues.length,
        })}
        <span className="narmi-icon-chevron-right" />
      </Button>
      <Dialog
        width="500px"
        isOpen={isDialogOpen}
        onUserDismiss={() => setIsDialogOpen(false)}
        title={l10n.getString("ach-passthrough-originating-accounts")}
        testId="nacha-passthrough-accounts-dialog"
      >
        <div className={styles.passthroughAccountList}>
          {accountValues.map(renderAccount)}
        </div>
      </Dialog>
    </>
  );
};

const NachaPassthroughTopCard = ({
  details,
  submissionId,
  deliveryCopy,
  handleClickViewPayments,
}: NachaPassthroughTopCardProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();

  const paymentCount = details.paymentCreditCount + details.paymentDebitCount;
  const netAmount = details.debitAmount - details.creditAmount;
  const effectiveDates = details.effectiveDates.map(formatDate);
  const paymentDetailRows: DrawerRowProps[] = [
    [
      l10n.getString("label-account"),
      details.originatingAccounts.length ? (
        <OriginatingAccountValues
          originatingAccounts={details.originatingAccounts}
        />
      ) : undefined,
    ],
    [
      l10n.getString("label-company-name"),
      details.companyNames.length ? (
        <DetailValues
          getLabel={(count) =>
            l10n.getString("ach-passthrough-overflow-company-names", { count })
          }
          values={details.companyNames}
        />
      ) : undefined,
    ],
    [
      l10n.getString("label-company-id"),
      details.companyIds.length ? (
        <DetailValues
          getLabel={(count) =>
            l10n.getString("ach-passthrough-overflow-company-ids", { count })
          }
          values={details.companyIds}
        />
      ) : undefined,
    ],
    [
      l10n.getString("ach-passthrough-sec-codes"),
      details.secCodes.length ? (
        <DetailValues
          getLabel={(count) =>
            l10n.getString("ach-passthrough-overflow-sec-codes", { count })
          }
          values={details.secCodes}
        />
      ) : undefined,
    ],
    [
      l10n.getString("label-entry-description"),
      details.entryDescriptions.length ? (
        <DetailValues
          getLabel={(count) =>
            l10n.getString("ach-passthrough-overflow-entry-descriptions", {
              count,
            })
          }
          values={details.entryDescriptions}
        />
      ) : undefined,
    ],
  ];

  const handleDownloadFile = async () => {
    try {
      const response = await fetch(
        `/v1/ach_nacha_passthrough/${submissionId}/download/`,
        { credentials: "include" }
      );
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      triggerBlobDownload(await response.blob(), details.fileName);
    } catch {
      sendNotification({
        type: "negative",
        text: l10n.getString("error-generic"),
      });
    }
  };

  return (
    <>
      <div className="margin--top--l">
        <ContentCard kind="bordered">
          <h5 className="fontSize--m fontWeight--normal margin--bottom--xs">
            {details.paymentTitle}
          </h5>
          <h1 className="fontColor--primary">
            {formatCurrencyFromCents(netAmount)}
          </h1>
          <hr className="margin--bottom--l margin--top--l" />

          <h5 className={styles.drawerLabelHeading}>
            {l10n.getString("ach-passthrough-validation-check-effective-date")}
          </h5>
          <div className="margin--bottom--m">{effectiveDates.join(", ")}</div>

          <h5 className={styles.drawerLabelHeading}>
            {l10n.getString("ach-passthrough-review-processing-date")}
          </h5>
          <div className="margin--bottom--m">
            {formatDate(details.processingDate)}
          </div>

          <h5 className={styles.drawerLabelHeading}>
            {l10n.getString("ach-passthrough-review-sending")}
          </h5>
          <div className="margin--bottom--m">
            {l10n.getString("ach-passthrough-review-direction-detail", {
              amount: formatCurrencyFromCents(details.creditAmount),
              count: details.paymentCreditCount,
            })}
          </div>

          <h5 className={styles.drawerLabelHeading}>
            {l10n.getString("ach-passthrough-review-collecting")}
          </h5>
          <div className="margin--bottom--m">
            {l10n.getString("ach-passthrough-review-direction-detail", {
              amount: formatCurrencyFromCents(details.debitAmount),
              count: details.paymentDebitCount,
            })}
          </div>

          <h5 className={styles.drawerLabelHeading}>
            {l10n.getString("label-payment-method")}
          </h5>
          <div className="margin--bottom--m">
            {l10n.getString("file-upload-type-passthrough-title")}
          </div>

          <h5 className={styles.drawerLabelHeading}>
            {l10n.getString("label-file-details")}
          </h5>
          <div className="margin--bottom--s">
            <Button
              kind="plain"
              size="s"
              onClick={handleClickViewPayments}
              disabled={!handleClickViewPayments}
              testId="nacha-passthrough-view-payments"
            >
              {l10n.getString("view-payments-cta", { total: paymentCount })}
              <span className="narmi-icon-chevron-right" />
            </Button>
          </div>

          <h5 className={styles.drawerLabelHeading}>
            {l10n.getString("label-file")}
          </h5>
          <Button
            kind="plain"
            size="s"
            onClick={handleDownloadFile}
            testId="nacha-passthrough-download-file"
          >
            <span className="narmi-icon-download" />
            <span className="margin--left--xs">{details.fileName}</span>
          </Button>
        </ContentCard>
      </div>
      {deliveryCopy ? (
        <div className="fontSize--xs margin--top--xs">{deliveryCopy}</div>
      ) : null}
      <div className="margin--top--xl">
        <h5 className="margin--bottom--xs fontColor--secondary fontWeight--bold">
          {l10n
            .getString("ach-passthrough-review-payment-details-heading")
            .toUpperCase()}
        </h5>
        <ContentCard kind="bordered">
          <DrawerRows rows={paymentDetailRows} />
        </ContentCard>
      </div>
    </>
  );
};

export default NachaPassthroughTopCard;
